import {
  LayoutDashboard,
  ShoppingBag,
  History,
  Star,
  UtensilsCrossed,
  Store,
  MapPin,
  Settings,
  ChevronsLeft,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router";
import { cn } from "~/lib/utils/cn";

interface SidebarProps {
  open: boolean;
  collapsed: boolean;
  onClose: () => void;
  onToggleCollapse: () => void;
}

interface NavItem {
  labelKey: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  end?: boolean;
}

const navItems: NavItem[] = [
  {
    labelKey: "nav.dashboard",
    path: "/",
    icon: LayoutDashboard,
    end: true,
  },
  {
    labelKey: "nav.orders",
    path: "/orders",
    icon: ShoppingBag,
    end: true,
  },
  {
    labelKey: "nav.orderHistory",
    path: "/orders/history",
    icon: History,
  },
  {
    labelKey: "nav.reviews",
    path: "/reviews",
    icon: Star,
  },
  {
    labelKey: "nav.menu",
    path: "/menu",
    icon: UtensilsCrossed,
  },
  {
    labelKey: "nav.profile",
    path: "/profile",
    icon: Store,
  },
  {
    labelKey: "nav.cityConfigs",
    path: "/city-configs",
    icon: MapPin,
  },
  {
    labelKey: "nav.settings",
    path: "/settings",
    icon: Settings,
  },
];

export function Sidebar({
  open,
  collapsed,
  onClose,
  onToggleCollapse,
}: SidebarProps) {
  const { t } = useTranslation();

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          "fixed inset-y-0 start-0 z-50 flex flex-col border-e border-sidebar-border bg-sidebar transition-all lg:static lg:ltr:translate-x-0 lg:rtl:translate-x-0",
          collapsed ? "lg:w-16" : "lg:w-60",
          "w-60",
          open
            ? "translate-x-0"
            : "ltr:-translate-x-full rtl:translate-x-full",
        )}
      >
        <div
          className={cn(
            "flex h-16 shrink-0 items-center border-b border-sidebar-border px-4",
            collapsed ? "lg:justify-center lg:px-0" : "justify-between",
          )}
        >
          <span
            className={cn(
              "text-xl font-bold text-sidebar-primary transition-opacity",
              collapsed && "lg:hidden",
            )}
          >
            Tazmingo
          </span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-sidebar-foreground hover:bg-sidebar-accent lg:hidden"
            aria-label={t("common.close")}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className={cn("flex-1 overflow-y-auto p-3", collapsed && "lg:p-2")}>
          <ul className="space-y-1">
            {navItems.map((item) => (
              <SidebarItem
                key={item.path}
                item={item}
                collapsed={collapsed}
                onNavigate={onClose}
              />
            ))}
          </ul>
        </nav>

        <div className="mt-auto border-t border-sidebar-border">
          <a
            href="https://level-app.co.il"
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "flex items-center gap-2 px-4 py-3 text-xs text-sidebar-foreground/60 transition-colors hover:text-sidebar-foreground",
              collapsed && "lg:justify-center lg:px-0",
            )}
          >
            <img
              src="/level-logo.png"
              alt="Level App"
              className="h-5 w-5 shrink-0 rounded"
            />
            <span
              className={cn(
                "transition-opacity",
                collapsed && "lg:hidden",
              )}
            >
              {t("common.poweredBy")}
            </span>
          </a>
        </div>

        <div className="hidden border-t border-sidebar-border p-2 lg:block">
          <button
            type="button"
            onClick={onToggleCollapse}
            className={cn(
              "flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
              collapsed && "justify-center px-0",
            )}
            aria-label={
              collapsed ? t("nav.expandSidebar") : t("nav.collapseSidebar")
            }
          >
            <ChevronsLeft
              className={cn(
                "h-5 w-5 shrink-0 transition-transform",
                collapsed && "ltr:rotate-180 rtl:rotate-0",
                !collapsed && "ltr:rotate-0 rtl:rotate-180",
              )}
            />
            <span
              className={cn(
                "ms-3 transition-opacity",
                collapsed && "lg:hidden",
              )}
            >
              {t("nav.collapseSidebar")}
            </span>
          </button>
        </div>
      </aside>
    </>
  );
}

function SidebarItem({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate: () => void;
}) {
  const { t } = useTranslation();

  if (collapsed) {
    return (
      <li className="hidden lg:block">
        <NavLink
          to={item.path}
          end={item.end}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              "flex items-center justify-center rounded-lg p-2.5 text-sm font-medium transition-colors",
              isActive
                ? "bg-sidebar-primary text-sidebar-primary-foreground"
                : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
            )
          }
          title={t(item.labelKey)}
        >
          <item.icon className="h-5 w-5 shrink-0" />
        </NavLink>
      </li>
    );
  }

  return (
    <li>
      <NavLink
        to={item.path}
        end={item.end}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
            isActive
              ? "bg-sidebar-primary text-sidebar-primary-foreground"
              : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
          )
        }
      >
        <item.icon className="h-5 w-5 shrink-0" />
        <span>{t(item.labelKey)}</span>
      </NavLink>
    </li>
  );
}
